import {
  MONEY_FIELD_RULE,
  QUANTITY_FIELD_RULE,
  decimalInputError,
  type DecimalFieldRule,
} from './arithmetic';
import type {
  Prediction,
  ReferenceField,
  ReviewDocument,
  ReviewDocumentRequest,
  ReviewFieldName,
  SalesLine,
} from './schema';

const NOTE_DATE_FIELD_PATH = '/note_date';

/**
 * The owner's in-progress edit of one note, and the pure operations 6.4's acceptance criteria
 * name: field edits, add/delete/reorder rows, annotations, and the request body a save actually
 * sends. Kept free of React and of `fetch` so every operation here is unit-testable without
 * mounting a screen.
 *
 * Money and quantity fields stay raw strings the whole way through - exactly what the owner
 * typed, exactly what `backend/app/domain/numbers.py` stores (trailing zeros and all). They are
 * never parsed to a JS number; `review/arithmetic.ts` reads them through `decimal.js` only for
 * display, never for what gets sent.
 */

export interface DraftReferenceField {
  /** `''` represents "nothing written" - `documentRequestFromDraft` turns it back into `null`. */
  readonly recognizedText: string;
  /** Set together with `referenceValueId`, or cleared together with it, only through
   * `updateCustomerReference`/`updateLineProductReference` (6.5) - never one without the other,
   * matching the pair rule `backend/app/reviews/references.py::check_official_values` enforces
   * server-side. Editing `recognizedText` alone (`updateCustomerText`/`updateLine`) never
   * touches these two. */
  readonly officialName: string | null;
  readonly referenceValueId: string | null;
}

/** What the owner picked in a reference picker: an official value's name and id together, or
 * `null` to clear the resolution - the only two shapes `updateCustomerReference`/
 * `updateLineProductReference` accept. */
export interface ReferenceResolution {
  readonly officialName: string;
  readonly referenceValueId: string;
}

export interface DraftLine {
  readonly lineKey: string;
  readonly product: DraftReferenceField;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly lineAmount: string;
}

export interface DraftAnnotation {
  /** Local identity for the React list and for deletion. Never sent - the server has no
   * per-annotation id (`ReviewDocument.annotations` is an unordered set of remarks). */
  readonly clientId: string;
  readonly text: string;
  readonly lineKey: string | null;
  readonly fieldName: ReviewFieldName | null;
}

export interface ReviewDraft {
  readonly customer: DraftReferenceField;
  /** `''` represents "no date" - an `<input type="date">`'s own empty value. */
  readonly noteDate: string;
  /** The pad's pre-printed serial number, `''` when the note has none. Kept as the owner typed
   * it and never parsed: a folio identifies a sheet of paper, so its leading zero matters and
   * there is no arithmetic to do on it. */
  readonly folio: string;
  readonly lines: readonly DraftLine[];
  readonly writtenTotal: string;
  readonly annotations: readonly DraftAnnotation[];
}

let clientIdCounter = 0;

/** A new client-only identity, readable and short-lived (this tab's session only). Not
 * `crypto.randomUUID()`: nothing outside this module ever reads it, and a counter is enough to
 * keep React list keys stable and is trivially deterministic for tests. */
function nextClientId(prefix: string): string {
  clientIdCounter += 1;
  return `${prefix}-${String(clientIdCounter)}`;
}

/** A fresh row identity for a line the owner adds, matching
 * `backend/app/domain/review_document.py`'s `LINE_KEY_PATTERN`
 * (`^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$`): starts with a letter, then a UUID, well under the
 * 64-character limit and outside any prefix recognition assigns (`row-NNN`). */
export function generateLineKey(): string {
  return `nueva-${crypto.randomUUID()}`;
}

function draftReferenceField(field: ReferenceField): DraftReferenceField {
  return {
    recognizedText: field.recognized_text ?? '',
    officialName: field.official_name,
    referenceValueId: field.reference_value_id,
  };
}

function requestReferenceField(field: DraftReferenceField): ReferenceField {
  return {
    recognized_text: field.recognizedText === '' ? null : field.recognizedText,
    official_name: field.officialName,
    reference_value_id: field.referenceValueId,
  };
}

function draftLine(line: SalesLine): DraftLine {
  return {
    lineKey: line.line_key,
    product: draftReferenceField(line.product),
    quantity: line.quantity ?? '',
    unitPrice: line.unit_price ?? '',
    lineAmount: line.line_amount ?? '',
  };
}

function requestLine(line: DraftLine): SalesLine {
  return {
    line_key: line.lineKey,
    product: requestReferenceField(line.product),
    quantity: line.quantity === '' ? null : line.quantity,
    unit_price: line.unitPrice === '' ? null : line.unitPrice,
    line_amount: line.lineAmount === '' ? null : line.lineAmount,
  };
}

/** An empty, untouched row for "Agregar fila" - unresolved product, every number blank. */
export function blankLine(): DraftLine {
  return {
    lineKey: generateLineKey(),
    product: { recognizedText: '', officialName: null, referenceValueId: null },
    quantity: '',
    unitPrice: '',
    lineAmount: '',
  };
}

/** The best guess for a note's date when recognition read one but nothing has resolved it: the
 * day-first calendar reading Stage A/B's own candidate list leads with
 * (`app.validation.dates.DateAssessment.preferred`, carried to the frontend as the `/note_date`
 * prediction's `candidates[0]` - `app.runs.predictions._date_candidates` orders it best-supported
 * first). Offered as a starting point the owner confirms or corrects, never as a silent answer:
 * a delivery still requires a date (`IncompleteForDeliveryError`), and the finding this same
 * prediction carries (`date_ambiguous`, `date_unparsed`, ...) is what tells the owner to check
 * it rather than trust it blindly. `null` when recognition read no plausible date at all, which
 * leaves the field exactly as blank as before this existed. */
export function bestDateCandidate(predictions: readonly Prediction[]): string | null {
  const prediction = predictions.find((candidate) => candidate.field_path === NOTE_DATE_FIELD_PATH);
  return prediction?.candidates[0] ?? null;
}

/** Builds the editable draft from a loaded (or just-saved) document. `predictions` is the run's
 * own readings, used only to pre-fill `noteDate` with a suggestion - every other field comes
 * from `document` alone, exactly as saved. */
export function draftFromDocument(
  document: ReviewDocument,
  predictions: readonly Prediction[] = [],
): ReviewDraft {
  return {
    customer: draftReferenceField(document.customer),
    noteDate: document.note_date ?? bestDateCandidate(predictions) ?? '',
    // No prediction fallback here, unlike `noteDate`: the server already fills the folio into
    // the baseline document it renders from the run (`app/reviews/repositories.py`), so a
    // second guess on this side could only disagree with it.
    folio: document.folio ?? '',
    lines: document.lines.map(draftLine),
    writtenTotal: document.written_total ?? '',
    annotations: document.annotations.map((annotation) => ({
      clientId: nextClientId('loaded'),
      text: annotation.text,
      lineKey: annotation.line_key,
      fieldName: annotation.field_name,
    })),
  };
}

/** The exact body `PUT /notes/{id}/review` sends for `draft`, given the response was validated
 * with `based_on_revision`. Field order is fixed so two calls over equal content always produce
 * identical `JSON.stringify` output - `isDraftDirty` depends on that. */
export function documentRequestFromDraft(draft: ReviewDraft): ReviewDocumentRequest {
  return {
    schema_version: 2,
    customer: requestReferenceField(draft.customer),
    note_date: draft.noteDate === '' ? null : draft.noteDate,
    folio: draft.folio.trim() === '' ? null : draft.folio.trim(),
    lines: draft.lines.map(requestLine),
    written_total: draft.writtenTotal === '' ? null : draft.writtenTotal,
    annotations: draft.annotations.map((annotation) => ({
      text: annotation.text,
      line_key: annotation.lineKey,
      field_name: annotation.fieldName,
    })),
  };
}

/** Whether `draft` differs from the document it was last loaded or saved from - the unsaved-
 * changes signal the screen uses to enable "Guardar cambios" and to warn before navigating away.
 * Compares the two documents' wire shape rather than the drafts themselves, so a value that
 * round-trips identically (loaded, then typed back exactly as it was) is correctly "not dirty". */
export function isDraftDirty(
  draft: ReviewDraft,
  savedDocument: ReviewDocument,
  predictions: readonly Prediction[] = [],
): boolean {
  const current = JSON.stringify(documentRequestFromDraft(draft));
  const saved = JSON.stringify(
    documentRequestFromDraft(draftFromDocument(savedDocument, predictions)),
  );
  return current !== saved;
}

/** A rough count of which top-level fields changed between `current` and the last saved
 * document - what the redesigned bottom action bar's "N cambios sin guardar" reports. A row
 * added, removed, or reordered counts as one change for that position; each differing leaf
 * field on an otherwise-matching row counts on its own, so editing three fields on one row
 * reads as three changes, matching what the owner actually typed. */
export function countDirtyFields(
  current: ReviewDraft,
  savedDocument: ReviewDocument,
  predictions: readonly Prediction[] = [],
): number {
  const saved = draftFromDocument(savedDocument, predictions);
  let count = 0;
  if (current.customer.recognizedText !== saved.customer.recognizedText) count += 1;
  if (current.customer.officialName !== saved.customer.officialName) count += 1;
  if (current.noteDate !== saved.noteDate) count += 1;
  // Trimmed on both sides, exactly as `documentRequestFromDraft` sends it: a trailing space
  // the owner typed is not a change, and counting it as one would disagree with
  // `isDraftDirty` about whether there is anything to save at all.
  if (current.folio.trim() !== saved.folio.trim()) count += 1;
  if (current.writtenTotal !== saved.writtenTotal) count += 1;

  const maxLines = Math.max(current.lines.length, saved.lines.length);
  for (let index = 0; index < maxLines; index += 1) {
    const a = current.lines[index];
    const b = saved.lines[index];
    if (a === undefined || b === undefined) {
      count += 1; // a row was added or removed at this position
      continue;
    }
    if (a.lineKey !== b.lineKey) {
      count += 1; // rows were reordered
      continue;
    }
    if (a.product.recognizedText !== b.product.recognizedText) count += 1;
    if (a.product.officialName !== b.product.officialName) count += 1;
    if (a.quantity !== b.quantity) count += 1;
    if (a.unitPrice !== b.unitPrice) count += 1;
    if (a.lineAmount !== b.lineAmount) count += 1;
  }

  if (current.annotations.length !== saved.annotations.length) count += 1;
  return count;
}

// --- row operations ------------------------------------------------------------------------

export function addLine(draft: ReviewDraft): ReviewDraft {
  return { ...draft, lines: [...draft.lines, blankLine()] };
}

/** Removes a row, and any annotation that pointed at it - a dangling `line_key` is refused by
 * the server (`ReviewDocument._annotations_point_at_real_rows`), so leaving one behind would
 * turn this delete into a save failure the owner cannot explain from the error message alone. */
export function removeLine(draft: ReviewDraft, lineKey: string): ReviewDraft {
  return {
    ...draft,
    lines: draft.lines.filter((line) => line.lineKey !== lineKey),
    annotations: draft.annotations.filter((annotation) => annotation.lineKey !== lineKey),
  };
}

function swap<T>(items: readonly T[], indexA: number, indexB: number): readonly T[] {
  const next = [...items];
  const itemA = next[indexA];
  const itemB = next[indexB];
  if (itemA === undefined || itemB === undefined) {
    return items;
  }
  next[indexA] = itemB;
  next[indexB] = itemA;
  return next;
}

export function moveLineUp(draft: ReviewDraft, lineKey: string): ReviewDraft {
  const index = draft.lines.findIndex((line) => line.lineKey === lineKey);
  if (index <= 0) {
    return draft;
  }
  return { ...draft, lines: swap(draft.lines, index, index - 1) };
}

export function moveLineDown(draft: ReviewDraft, lineKey: string): ReviewDraft {
  const index = draft.lines.findIndex((line) => line.lineKey === lineKey);
  if (index === -1 || index >= draft.lines.length - 1) {
    return draft;
  }
  return { ...draft, lines: swap(draft.lines, index, index + 1) };
}

export function updateLine(
  draft: ReviewDraft,
  lineKey: string,
  patch: Partial<Omit<DraftLine, 'lineKey' | 'product'>> & {
    readonly productText?: string;
  },
): ReviewDraft {
  return {
    ...draft,
    lines: draft.lines.map((line) => {
      if (line.lineKey !== lineKey) {
        return line;
      }
      return {
        ...line,
        ...(patch.quantity !== undefined ? { quantity: patch.quantity } : {}),
        ...(patch.unitPrice !== undefined ? { unitPrice: patch.unitPrice } : {}),
        ...(patch.lineAmount !== undefined ? { lineAmount: patch.lineAmount } : {}),
        ...(patch.productText !== undefined
          ? { product: { ...line.product, recognizedText: patch.productText } }
          : {}),
      };
    }),
  };
}

export function updateCustomerText(draft: ReviewDraft, text: string): ReviewDraft {
  return { ...draft, customer: { ...draft.customer, recognizedText: text } };
}

/** The owner resolves (or clears) the customer's official value, from a reference picker
 * (6.5). `resolution` sets `officialName`/`referenceValueId` together; `null` clears both
 * together - there is no way to set one without the other through this function. */
export function updateCustomerReference(
  draft: ReviewDraft,
  resolution: ReferenceResolution | null,
): ReviewDraft {
  return {
    ...draft,
    customer: {
      ...draft.customer,
      officialName: resolution?.officialName ?? null,
      referenceValueId: resolution?.referenceValueId ?? null,
    },
  };
}

/** The owner resolves (or clears) one row's product official value, from a reference picker
 * (6.5). Same pair rule as `updateCustomerReference`. */
export function updateLineProductReference(
  draft: ReviewDraft,
  lineKey: string,
  resolution: ReferenceResolution | null,
): ReviewDraft {
  return {
    ...draft,
    lines: draft.lines.map((line) => {
      if (line.lineKey !== lineKey) {
        return line;
      }
      return {
        ...line,
        product: {
          ...line.product,
          officialName: resolution?.officialName ?? null,
          referenceValueId: resolution?.referenceValueId ?? null,
        },
      };
    }),
  };
}

export function updateNoteDate(draft: ReviewDraft, noteDate: string): ReviewDraft {
  return { ...draft, noteDate };
}

export function updateFolio(draft: ReviewDraft, folio: string): ReviewDraft {
  return { ...draft, folio };
}

export function updateWrittenTotal(draft: ReviewDraft, writtenTotal: string): ReviewDraft {
  return { ...draft, writtenTotal };
}

// --- annotations -----------------------------------------------------------------------------

export interface NewAnnotation {
  readonly text: string;
  readonly lineKey: string | null;
  readonly fieldName: ReviewFieldName | null;
}

export function addAnnotation(draft: ReviewDraft, annotation: NewAnnotation): ReviewDraft {
  const trimmed = annotation.text.trim();
  if (trimmed === '') {
    return draft;
  }
  return {
    ...draft,
    annotations: [
      ...draft.annotations,
      {
        clientId: nextClientId('new'),
        text: trimmed,
        lineKey: annotation.lineKey,
        fieldName: annotation.fieldName,
      },
    ],
  };
}

export function removeAnnotation(draft: ReviewDraft, clientId: string): ReviewDraft {
  return { ...draft, annotations: draft.annotations.filter((a) => a.clientId !== clientId) };
}

// --- validation --------------------------------------------------------------------------------

export interface DraftFieldError {
  readonly fieldPath: string;
  readonly message: string;
}

function fieldErrors(path: string, value: string, rule: DecimalFieldRule): DraftFieldError | null {
  const message = decimalInputError(value, rule);
  return message === null ? null : { fieldPath: path, message };
}

/** Every decimal field in `draft` that is not empty and not an exact valid decimal string, in
 * document order. Non-empty means "Guardar cambios" must stay disabled - saving a value the
 * owner cannot see is exact would defeat the point of typing it. */
export function draftValidationErrors(draft: ReviewDraft): readonly DraftFieldError[] {
  const errors: DraftFieldError[] = [];
  const totalError = fieldErrors('/written_total', draft.writtenTotal, MONEY_FIELD_RULE);
  if (totalError) {
    errors.push(totalError);
  }
  for (const line of draft.lines) {
    const quantityError = fieldErrors(
      `/lines/${line.lineKey}/quantity`,
      line.quantity,
      QUANTITY_FIELD_RULE,
    );
    if (quantityError) {
      errors.push(quantityError);
    }
    const priceError = fieldErrors(
      `/lines/${line.lineKey}/unit_price`,
      line.unitPrice,
      MONEY_FIELD_RULE,
    );
    if (priceError) {
      errors.push(priceError);
    }
    const amountError = fieldErrors(
      `/lines/${line.lineKey}/line_amount`,
      line.lineAmount,
      MONEY_FIELD_RULE,
    );
    if (amountError) {
      errors.push(amountError);
    }
  }
  return errors;
}
